/**
 * Dr Cleanstein over whatever page the person is on. On a computer it opens as a
 * large centred window laid out like the Ask page: chats down the left, the open
 * chat filling the rest. On a phone it fills the screen and Chats swaps the
 * conversation for the person's chat list.
 * The open chat is remembered for the session, so closing and reopening carries
 * on where it left off. Full screen opens the same chat on the Ask page.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AskChat from './AskChat';
import AskChatList from './AskChatList';
import { AVATAR_SRC, type ConversationSummary } from './askApi';
import { STAFF_ASSISTANT_NAME } from '../../config/assistant';

const SESSION_KEY = 'ask-panel-conversation';
// Matches the portal's phone breakpoint (max-width: 768px).
const WIDE_QUERY = '(min-width: 769px)';

function useWide(): boolean {
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.matchMedia(WIDE_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(WIDE_QUERY);
    const onChange = () => setWide(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return wide;
}

function readSaved(): string | null {
  try { return sessionStorage.getItem(SESSION_KEY); } catch { return null; }
}

function save(id: string | null) {
  try {
    if (id) sessionStorage.setItem(SESSION_KEY, id);
    else sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // private window: the panel simply starts fresh next time
  }
}

export default function AskPanel({ onClose }: { onClose: () => void }) {
  const [conversationId, setConversationId] = useState<string | null>(readSaved);
  const [showList, setShowList] = useState(false);
  const [chats, setChats] = useState<ConversationSummary[]>([]);
  // Bumped after each answer, so the window's chat list re-sorts the chat to the top of Today.
  const [answered, setAnswered] = useState(0);
  const wide = useWide();

  const choose = (id: string | null) => {
    setConversationId(id);
    save(id);
    setShowList(false);
  };

  useEffect(() => {
    // Escape closes the panel, unless a modal opened from it (Log a query) is on top.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.querySelector('.modal-overlay')) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const title = chats.find((c) => c.id === conversationId)?.title ?? null;

  const chat = (
    <AskChat
      variant={wide ? 'page' : 'panel'}
      autoFocus
      conversationId={conversationId}
      onConversationChange={(id) => { setConversationId(id); save(id); }}
      title={title}
      onAnswered={() => setAnswered((n) => n + 1)}
    />
  );

  return (
    <>
      <div className="side-panel-overlay" onClick={onClose} />
      <aside className="side-panel side-panel--wide ask-window" role="dialog" aria-modal="true" aria-label={STAFF_ASSISTANT_NAME}>
        <div className="side-panel-header">
          <img src={AVATAR_SRC} alt="" className="ask-avatar ask-panel-avatar" />
          <h2 className="side-panel-title">{STAFF_ASSISTANT_NAME}</h2>
          {!wide && (
            <>
              <button type="button" className="ask-ghost-btn" onClick={() => choose(null)}>+ New</button>
              <button type="button" className={`ask-ghost-btn ${showList ? 'active' : ''}`} aria-pressed={showList} onClick={() => setShowList((v) => !v)}>
                Chats
              </button>
            </>
          )}
          <Link
            to={conversationId ? `/ask?c=${conversationId}` : '/ask'}
            className="ask-ghost-btn ask-fullscreen"
            onClick={onClose}
            title="Open full screen"
          >
            Full screen
          </Link>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="side-panel-body">
          {/* One layout tree for both sizes, so turning a tablet or resizing the
              window never restarts the chat (an unsent question or a half-read
              answer would be lost). */}
          <div className={wide ? 'ask-page' : 'ask-window-body'}>
            {wide && (
              <aside className="ask-sidebar">
                <button type="button" className="ask-new-chat" onClick={() => choose(null)}>
                  <span aria-hidden="true">+</span> New chat
                </button>
                <AskChatList activeId={conversationId} onOpen={choose} refreshKey={`${conversationId ?? ''}:${answered}`} onLoaded={setChats} />
              </aside>
            )}
            <section className="ask-main">
              {!wide && showList ? (
                <AskChatList activeId={conversationId} onOpen={choose} onLoaded={setChats} />
              ) : (
                chat
              )}
            </section>
          </div>
        </div>
      </aside>
    </>
  );
}
